import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { InAppBrowser } from '@capacitor/inappbrowser'
import type { URLOpenListenerEvent } from '@capacitor/app'

// Global state to prevent multiple plugin initializations
let hasInitialized = false

export default defineNuxtPlugin(nuxtApp => {
  if (!import.meta.client) return
  
  // Prevent multiple initializations (hot reload, etc.)
  if (hasInitialized) {
    return
  }
  hasInitialized = true

  // Debug overlay utility
  // const ensureDebugOverlay = () => {
  //   let el = document.getElementById('capacitor-url-debug')
  //   if (!el) {
  //     el = document.createElement('div')
  //     el.id = 'capacitor-url-debug'
  //     el.style.position = 'fixed'
  //     el.style.bottom = '10px'
  //     el.style.left = '10px'
  //     el.style.background = 'rgba(0,0,0,0.8)'
  //     el.style.color = '#fff'
  //     el.style.padding = '8px 12px'
  //     el.style.zIndex = '9999'
  //     el.style.fontSize = '14px'
  //     el.style.borderRadius = '4px'
  //     el.textContent = 'Capacitor Debug: Waiting for event...'
  //     document.body.appendChild(el)
  //   }
  //   return el
  // }

  const customDebugLog = (msg: string) => {
    // const el = ensureDebugOverlay()
    // const now = new Date().toLocaleTimeString()
    // el.textContent = `[${now}] Capacitor Debug: ${msg}`

    // Persist to localStorage
    // try {
    //   const LOG_KEY = 'cc_capacitor_logs_v1'
    //   const payload = {
    //     ts: new Date().toISOString(),
    //     level: 'debug',
    //     message: msg
    //   }

    //   const raw = localStorage.getItem(LOG_KEY)
    //   const logs = raw ? (JSON.parse(raw) as typeof payload[]) : [] as typeof payload[]

    //   logs.push(payload)
    //   // Keep last 2000 entries to avoid storage bloat
    //   localStorage.setItem(LOG_KEY, JSON.stringify(logs.slice(-2000)))
    // } catch {
    // // If storage is unavailable or quota exceeded, fail silently
    // }
  
    // eslint-disable-next-line no-console
    console.log(`Capacitor Debug: ${msg}`)
  }

  const navigateSafely = async (redirectPath: string, replace = false) => {
    const router = useRouter()
    // Ensure router is ready before navigating (especially for cold start deep links)
    if ((router as any).isReady) {
      try {
        await (router as any).isReady()
      } catch {
        // ignore
      }
    } else {
      // Fallback small delay if isReady not exposed (older Nuxt internals)
      await new Promise(r => setTimeout(r, 10))
    }
    const current = router.currentRoute.value.fullPath
    if (current === redirectPath) {
      customDebugLog(`Navigation skipped (already at ${redirectPath})`)
      return
    }
    customDebugLog(`Navigating via router to ${redirectPath} (replace=${replace}) from ${current}`)
    try {
      if (replace) {
        await router.replace(redirectPath)
      } else {
        await router.push(redirectPath)
      }
      // Wait a tick and verify navigation succeeded
      await new Promise(r => setTimeout(r, 50))
      const afterNav = router.currentRoute.value.fullPath
      customDebugLog(`Navigation completed: now at ${afterNav}`)
      if (afterNav !== redirectPath && afterNav === '/') {
        customDebugLog('WARNING: Navigation failed, still at root. Trying window.location fallback.')
        window.location.href = redirectPath
      }
    } catch (e) {
      const msg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
      customDebugLog('Router navigation failed: ' + msg)
      // Fallback to hard navigation
      customDebugLog('Attempting window.location fallback')
      window.location.href = redirectPath
    }
  }

  // CLUB-480: finishes a Gmail connect started in the app (see the email-connect
  // branch below). Set when the native listeners are attached, in the plugin's
  // setup context, because its API service reads runtime config when constructed.
  let completeGmailConnect: ReturnType<typeof useGmailConsent>['completeGmailConnect'] | null = null
  // A completion code is single-use, so each email-connect link is handled once.
  const handledEmailConnectLinks = new Set<string>()
  // Resolves once the next page has rendered (after the outgoing page's
  // transition), so the page loader can come down; or after 2s regardless.
  const nextPageShown = () => new Promise<void>(resolve => {
    const fallback = setTimeout(resolve, 2000)
    nuxtApp.hooks.hookOnce('page:finish', () => {
      clearTimeout(fallback)
      resolve()
    })
  })

  // Main handler for incoming URLs
  const handleUrl = async (urlString: string, isInitialLaunch = false) => {
    try {
      const router = useRouter()
      const url = new URL(urlString)
      customDebugLog(`Received URL: ${urlString} (isInitialLaunch=${isInitialLaunch})`)
      
      let redirectPath = url.pathname + url.search + url.hash

      // Handle urlScheme:// - compareclub://authorization/callback?code=...&state=...
      if ((url.protocol === 'compareclub:' || url.protocol === 'compareclub-dev:') && url.hostname === 'authorization' && url.pathname.startsWith('/callback')) {
        
        const currentPath = router.currentRoute.value.fullPath
        // Redirect to your Nuxt route handler
        redirectPath = `/dashboard/authorization/callback/${url.search}&origin=${Capacitor.getPlatform()}${url.hash}`
        customDebugLog('Current url: ' + currentPath)
        customDebugLog('Redirecting to callback path: ' + redirectPath)
        if (currentPath !== redirectPath) {
          // close the in-app browser
          try {
            await InAppBrowser.close()
          } catch (e) {
            const errorMsg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
            customDebugLog('Failed to close Capacitor Browser: ' + errorMsg)
          }
          navigateSafely(redirectPath)
        }
        return
      }

      // Handle urlScheme:// dashboard deep link - compareclub://dashboard
      // Fired by in-app browser pages (e.g. energy quote results) to return to the native app.
      if ((url.protocol === 'compareclub:' || url.protocol === 'compareclub-dev:') && url.hostname === 'dashboard') {
        customDebugLog('Dashboard deep link received; returning to native app')
        // Close the in-app browser so the user lands on the native dashboard, not behind it
        try {
          await InAppBrowser.close()
        } catch (e) {
          const errorMsg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
          customDebugLog('Failed to close Capacitor Browser: ' + errorMsg)
        }
        navigateSafely('/dashboard/', true)
        return
      }

      // CLUB-480: a Gmail connect started in the app comes back from the system
      // browser as compareclub(-dev)://email-connect/callback, with a completion
      // code or a failure gmail_status. Behind the page loader (the member lands
      // back on the consent page), finish the connect with the code as the
      // signed-in member, then open the processing page with the result.
      if (isEmailConnectAppLink(urlString)) {
        if (!completeGmailConnect || handledEmailConnectLinks.has(urlString)) return
        handledEmailConnectLinks.add(urlString)
        customDebugLog('Email-connect result received; finishing the connect')
        await finishEmailConnectInApp(urlString, {
          complete: completeGmailConnect,
          closeBrowser: async () => {
            try {
              await InAppBrowser.close()
            } catch (e) {
              const errorMsg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
              customDebugLog('Failed to close Capacitor Browser: ' + errorMsg)
            }
          },
          navigate: path => navigateSafely(path),
          pageLoader: useLayoutsStore(),
          nextPageShown
        })
        return
      }

      // Skip if not compareclub.com.au domain
      if (!url.hostname.endsWith('compareclub.com.au') || url.pathname.startsWith('/dashboard/authorization/callback/')) {
        customDebugLog(`Skipping non-compareclub & invalid URL: ${url.hostname}`)
        return
      }

      customDebugLog(`Navigating to app path: ${redirectPath}`)
      // Use safe navigation. For initial launch, add delay to let auth/localStorage be ready
      if (isInitialLaunch) {
        // Wait longer for cold start: auth needs to initialize
        setTimeout(() => {
          navigateSafely(redirectPath, true)
        }, 300)
      } else {
        // App already running, navigate immediately
        navigateSafely(redirectPath)
      }
    } catch {
      customDebugLog(`Invalid URL: ${urlString}`)
    }
  }

  if (Capacitor.isNativePlatform()) {
    customDebugLog('Native platform detected, attaching listeners...')
    completeGmailConnect = useGmailConsent().completeGmailConnect

    // Listener for when the app is already open
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      customDebugLog(`appUrlOpen event: ${event.url}`)
      // When app is reopened from killed state, add delay for auth to load
      // Check if we're still at root (indicates cold start, not warm resume)
      const isColdStart = window.location.pathname === '/' || window.location.pathname === '/index.html'
      if (isColdStart) {
        customDebugLog('Cold start detected via appUrlOpen, delaying navigation for auth')
        setTimeout(() => {
          handleUrl(event.url, true)
        }, 300)
      } else {
        handleUrl(event.url, false)
      }
    })

    // Fallback listener: when app resumes, re-check launch URL (some push taps may only resume)
    // App.addListener('resume', async () => {
    //   customDebugLog('resume event: checking potential pending launch URL')
    //   try {
    //     const launchUrl = await App.getLaunchUrl()
    //     if (launchUrl?.url) {
    //       customDebugLog(`resume:getLaunchUrl found ${launchUrl.url}`)
    //       handleUrl(launchUrl.url)
    //     }
    //   } catch (e) {
    //     const msg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
    //     customDebugLog('resume:getLaunchUrl error: ' + msg)
    //   }
    // })

    // State change listener: if app becomes active after notification tap without url event
    // App.addListener('appStateChange', async (state: { isActive: boolean }) => {
    //   if (state.isActive) {
    //     customDebugLog('appStateChange->active: re-validating launch URL')
    //     try {
    //       const launchUrl = await App.getLaunchUrl()
    //       if (launchUrl?.url) {
    //         customDebugLog(`appStateChange:getLaunchUrl found ${launchUrl.url}`)
    //         handleUrl(launchUrl.url)
    //       }
    //     } catch (e) {
    //       const msg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
    //       customDebugLog('appStateChange:getLaunchUrl error: ' + msg)
    //     }
    //   }
    // })

    // Handle the initial launch URL of the capacitor app
    const handleAppLaunch = async () => {
      try {
        const launchUrl = await App.getLaunchUrl()
        customDebugLog(`App launched with URL: ${launchUrl?.url || 'none'}`)
        
        // If a launch URL exists, the appUrlOpen listener will handle it.
        // We just need to ensure we don't navigate away to /dashboard.
        if (launchUrl && launchUrl.url) {
          customDebugLog('Launch URL detected - appUrlOpen listener will handle navigation')
          return
        }
      } catch (e) {
        const msg = typeof e === 'object' && e !== null && 'message' in e ? (e as any).message : String(e)
        customDebugLog('getLaunchUrl error: ' + msg)
      }
      
      // Only navigate to /dashboard if NO launch URL and we're at root
      if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
        customDebugLog('No launch URL; redirecting to /dashboard')
        // Use hard navigation on cold start to avoid module loading race conditions
        window.location.replace('/dashboard')
      }
    }

    // Run the logic
    handleAppLaunch()
    
  } else {
    customDebugLog('Not running on an app native platform')
  }
})